/**
 * BroadcastModal — supervisor-to-team broadcast via /v3/staff/send-note.
 * The current api fanout is 1:1, so we iterate over the FIXTURE_PRESENCE for
 * a non-nuclear demo broadcast; Wave 3 will add `/v3/staff/broadcast` for
 * native group messaging.
 */

import { useState } from 'react';
import { Banner, Button, ModalShell } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { FIXTURE_PRESENCE } from '../fixtures';

export interface BroadcastModalProps {
  open: boolean;
  onClose: () => void;
}

export function BroadcastModal({ open, onClose }: BroadcastModalProps): JSX.Element | null {
  const api = useApiClient();
  const { user } = useAuth();
  const [message, setMessage] = useState<string>('');
  const [audience, setAudience] = useState<'allOn' | 'supv'>('allOn');
  const [sending, setSending] = useState<boolean>(false);
  const [status, setStatus] = useState<string | null>(null);

  async function send(): Promise<void> {
    if (!user?.upn || !message.trim()) return;
    setSending(true);
    setStatus(null);
    try {
      const recipients = FIXTURE_PRESENCE.filter((p) =>
        audience === 'supv' ? p.role === 'Supv' : p.presence !== 'offline',
      );
      const results = await Promise.allSettled(
        recipients.map((r) =>
          api.retail.staff.sendNote({
            fromEmail: user.upn,
            fromName: user.displayName,
            toEmail: `${r.initials.toLowerCase()}@mypcb.com`,
            toName: r.name,
            messageBody: `[BROADCAST] ${message}`,
            contextLabel: 'RPM-Broadcast',
          }),
        ),
      );
      const okCount = results.filter((r) => r.status === 'fulfilled').length;
      setStatus(`Sent to ${okCount} / ${recipients.length} recipients.`);
      setTimeout(() => { onClose(); setStatus(null); setMessage(''); }, 1500);
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Broadcast failed.');
    } finally {
      setSending(false);
    }
  }

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Broadcast to team"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={send} disabled={sending || !message.trim()}>
            {sending ? 'Broadcasting…' : '📢 Send'}
          </Button>
        </>
      }
    >
      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend style={{ fontSize: 12, fontWeight: 600, color: 'var(--pcb-blue-dark)', marginBottom: 'var(--sp-4)' }}>
          Audience
        </legend>
        <label style={{ fontSize: 13, marginRight: 'var(--sp-8)' }}>
          <input
            type="radio"
            name="rpm-audience"
            value="allOn"
            checked={audience === 'allOn'}
            onChange={() => setAudience('allOn')}
            style={{ marginRight: 6 }}
          />
          All on-shift staff
        </label>
        <label style={{ fontSize: 13 }}>
          <input
            type="radio"
            name="rpm-audience"
            value="supv"
            checked={audience === 'supv'}
            onChange={() => setAudience('supv')}
            style={{ marginRight: 6 }}
          />
          Supervisors only
        </label>
      </fieldset>

      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--pcb-blue-dark)' }}>
        Message
        <textarea
          rows={4}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="e.g. Pull idle folks from Rolla DT to help St. James finish the 2pm rush."
          style={{ width: '100%', padding: 'var(--sp-5)', border: '1px solid var(--surface-border)', borderRadius: 'var(--r-md)', marginTop: 4, fontFamily: 'inherit' }}
        />
      </label>

      {status && <Banner tone="ok">{status}</Banner>}
    </ModalShell>
  );
}
